import { ThemeName } from "./palette";

export interface ResolverEnv {
  // Lazy read: Office populates context.officeTheme only after Office.onReady,
  // so we must re-read it on every resolve rather than snapshot it by value.
  getOfficeTheme: () => { isDarkTheme?: boolean; bodyBackgroundColor?: string } | undefined;
  // Lazy read of the Teams client theme (pushed in via setTeamsTheme). Optional
  // so injected test envs and non-Teams pages need not provide it.
  getTeamsTheme?: () => ThemeName | null;
  prefersDark: () => boolean;
}

// Teams tabs have no Office.js and the Teams client theme is independent of the
// OS prefers-color-scheme, so teams-js pushes it here (getContext at startup,
// registerOnThemeChangeHandler for live switches). "contrast" is Teams' black-
// based high-contrast theme -> dark; anything else ("default", unknown) -> light.
let teamsTheme: ThemeName | null = null;
// Returns the normalized value so callers can persist it without a read-back.
export const setTeamsTheme = (theme: string | undefined | null): ThemeName | null => {
  teamsTheme = theme == null ? null : theme === "dark" || theme === "contrast" ? "dark" : "light";
  return teamsTheme;
};
export const getTeamsTheme = (): ThemeName | null => teamsTheme;

const srgbToLinear = (c: number): number => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

export const relativeLuminance = (hex: string): number => {
  const t = hex.trim();
  // Accept 3-digit shorthand (#abc) by expanding each nibble, plus full 6-hex.
  const short = /^#?([0-9a-f]{3})$/i.exec(t);
  const six = short
    ? short[1].split("").map((c) => c + c).join("")
    : (/^#?([0-9a-f]{6})$/i.exec(t)?.[1] ?? null);
  if (!six) return 1; // unparseable -> treat as light
  const int = parseInt(six, 16);
  const r = srgbToLinear((int >> 16) & 255);
  const g = srgbToLinear((int >> 8) & 255);
  const b = srgbToLinear(int & 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Background is "dark" when its luminance is below the mid-point. Tunable.
export const isDarkColor = (hex: string): boolean => relativeLuminance(hex) < 0.5;

export const resolveSystem = (env: ResolverEnv): ThemeName => {
  const ot = env.getOfficeTheme();
  if (ot) {
    if (typeof ot.isDarkTheme === "boolean") return ot.isDarkTheme ? "dark" : "light";
    if (ot.bodyBackgroundColor) return isDarkColor(ot.bodyBackgroundColor) ? "dark" : "light";
  }
  const tt = env.getTeamsTheme?.();
  if (tt) return tt;
  return env.prefersDark() ? "dark" : "light";
};

const mq = (): MediaQueryList | null =>
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;

export const getEnv = (): ResolverEnv => ({
  getOfficeTheme: () => {
    try {
      // eslint-disable-next-line no-undef
      return (window as any).Office?.context?.officeTheme;
    } catch {
      return undefined;
    }
  },
  getTeamsTheme: () => teamsTheme,
  prefersDark: () => !!mq()?.matches,
});

// Attaches the change sources that are available BEFORE Office.onReady:
// the OS media query and focus/visibility (Word/Excel have no theme event).
// The Outlook OfficeThemeChanged handler is registered separately, post-onReady,
// via subscribeOfficeTheme. Returns an unsubscribe.
export const subscribeSystem = (onChange: () => void): (() => void) => {
  const cleanups: Array<() => void> = [];

  // OS media query (fallback + always).
  const m = mq();
  if (m) {
    const h = () => onChange();
    if (m.addEventListener) { m.addEventListener("change", h); cleanups.push(() => m.removeEventListener("change", h)); }
    else { m.addListener(h); cleanups.push(() => m.removeListener(h)); }
  }

  // Word/Excel have no theme event -> re-resolve on focus/visibility.
  if (typeof document !== "undefined") {
    const h = () => onChange();
    document.addEventListener("visibilitychange", h);
    window.addEventListener("focus", h);
    cleanups.push(() => { document.removeEventListener("visibilitychange", h); window.removeEventListener("focus", h); });
  }

  return () => cleanups.forEach((c) => { try { c(); } catch { /* noop */ } });
};

// Registers ONLY the Outlook OfficeThemeChanged handler (Mailbox 1.14+, task
// pane only). Must be called AFTER Office.onReady — pre-onReady context.requirements
// is undefined so feature detection silently fails. Returns a cleanup, or null
// if the event is not registrable on this host.
export const subscribeOfficeTheme = (onChange: () => void): (() => void) | null => {
  try {
    const O = (window as any).Office;
    if (O?.context?.requirements?.isSetSupported?.("Mailbox", "1.14") && O.context.mailbox?.addHandlerAsync) {
      const h = () => onChange();
      O.context.mailbox.addHandlerAsync(O.EventType.OfficeThemeChanged, h);
      // Mailbox.removeHandlerAsync removes all handlers for the event type (no
      // per-handler scoping — that exists only on Binding/Document/Settings);
      // we register exactly one OfficeThemeChanged handler per page via the
      // singleton controller, so removing "all" equals removing ours.
      return () => { try { O.context.mailbox.removeHandlerAsync(O.EventType.OfficeThemeChanged); } catch { /* noop */ } };
    }
  } catch { /* not Outlook / API absent */ }
  return null;
};
